import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Flame, Home as HomeIcon, ShieldAlert, Timer as TimerIcon } from "lucide-react";
import { NovaOrb } from "../components/NovaOrb";
import { BottomNav } from "../components/BottomNav";
import { ReadinessCheckin } from "../components/mission/ReadinessCheckin";
import { ExerciseRow } from "../components/mission/ExerciseRow";
import { api } from "../lib/api";

const DAY_TYPE_BADGE = {
  GREEN: { label: "Full session", cls: "text-ready-green border-ready-green/40 bg-ready-green/10" },
  "GREEN-SHORT": { label: "Full · trimmed to time", cls: "text-ready-green border-ready-green/40 bg-ready-green/10" },
  YELLOW: { label: "Floor day", cls: "text-ready-amber border-ready-amber/40 bg-ready-amber/10" },
};

const formatClock = (secs) => {
  const m = Math.floor(secs / 60).toString().padStart(2, "0");
  const s = (secs % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
};

export default function Mission() {
  const navigate = useNavigate();
  const [mission, setMission] = useState(null);
  const [checkinRequired, setCheckinRequired] = useState(false);
  const [submittingReadiness, setSubmittingReadiness] = useState(false);
  const [variant, setVariant] = useState("full");
  const [exercises, setExercises] = useState([]);
  const [checked, setChecked] = useState({});
  const [completion, setCompletion] = useState(null);
  const [completing, setCompleting] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const timerRef = useRef(null);

  const loadMission = useCallback(async () => {
    const { data } = await api.get("/mission/today");
    if (data.needs_onboarding) {
      navigate("/onboarding");
      return;
    }
    if (data.checkin_required) {
      setCheckinRequired(true);
      setMission(null);
      return;
    }
    setCheckinRequired(false);
    setMission(data);
    const initialVariant = data.recommended_variant === "floor" ? "floor" : data.recommended_variant === "full_short" ? "full_short" : "full";
    setVariant(initialVariant);
    setExercises(initialVariant === "floor" ? data.floor_exercises : data.full_exercises);
    setChecked({});
    if (data.already_completed) {
      setCompletion({
        message: data.completed_variant === "floor" ? "Floor day logged — the chain lives on." : "Session logged. Small beats zero.",
        streak: data.streak,
        consistency_pct: data.consistency_pct,
      });
    } else {
      setCompletion(null);
    }
  }, [navigate]);

  useEffect(() => {
    loadMission();
  }, [loadMission]);

  useEffect(() => {
    if (mission && !completion) {
      timerRef.current = setInterval(() => setElapsed((e) => e + 1), 1000);
      return () => clearInterval(timerRef.current);
    }
  }, [mission, completion]);

  const submitReadiness = async (payload) => {
    setSubmittingReadiness(true);
    await api.post("/readiness/checkin", payload);
    setSubmittingReadiness(false);
    await loadMission();
  };

  const switchToFull = () => {
    setVariant("full");
    setExercises(mission.full_exercises);
    setChecked({});
  };

  const toggleCheck = (id) => setChecked((c) => ({ ...c, [id]: !c[id] }));

  const swapExercise = async (ex) => {
    const { data } = await api.post("/mission/swap-exercise", {
      exercise_id: ex.id,
      exclude_ids: exercises.map((e) => e.id),
      sets: ex.sets,
      reps: ex.reps,
    });
    if (data.alternate) {
      setExercises((list) => list.map((e) => (e.id === ex.id ? data.alternate : e)));
    }
  };

  const complete = async (level) => {
    setCompleting(true);
    const backendVariant = variant === "floor" ? "floor" : "full";
    const exercises_done = Object.keys(checked).filter((id) => checked[id]);
    const { data } = await api.post("/mission/complete", {
      session_id: mission.session_id,
      variant: backendVariant,
      level,
      exercises_done,
    });
    setCompleting(false);
    setCompletion(data);
    clearInterval(timerRef.current);
  };

  if (checkinRequired) {
    return (
      <div className="min-h-screen bg-kaizen-bg text-kaizen-text pb-28" data-testid="mission-page">
        <div className="max-w-md mx-auto px-5 pt-8">
          <div className="flex flex-col items-center mb-6">
            <NovaOrb size={90} />
          </div>
          <ReadinessCheckin onSubmit={submitReadiness} submitting={submittingReadiness} />
        </div>
        <BottomNav />
      </div>
    );
  }

  if (!mission) {
    return (
      <div className="min-h-screen bg-kaizen-bg flex items-center justify-center">
        <NovaOrb size={90} />
      </div>
    );
  }

  const badge = DAY_TYPE_BADGE[mission.day_type] || DAY_TYPE_BADGE.GREEN;

  return (
    <div className="min-h-screen bg-kaizen-bg text-kaizen-text pb-28" data-testid="mission-page">
      <div className="absolute inset-0 -z-10 h-[320px] bg-[radial-gradient(circle_at_50%_0%,rgba(139,93,199,0.16),transparent_65%)]" />

      <div className="max-w-md mx-auto px-5 pt-8">
        <div className="flex items-start justify-between">
          <NovaOrb size={80} />
          <div className="flex items-center gap-1.5 text-sm text-kaizen-violetSoft glass-card rounded-full px-3 py-1.5" data-testid="streak-badge">
            <Flame size={14} />
            <span>{mission.streak} day streak</span>
          </div>
        </div>

        <p className="mt-4 text-sm text-kaizen-dim" data-testid="nova-mission-line">{mission.nova_line}</p>

        <div className="glass-card rounded-3xl p-5 mt-5" data-testid="mission-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-widest text-kaizen-dim">{mission.day_label}</p>
              <p className="font-display text-xl text-kaizen-text mt-0.5">{mission.muscle_focus}</p>
            </div>
            <span className={`text-xs px-3 py-1 rounded-full border ${badge.cls}`} data-testid="day-type-badge">
              {badge.label}
            </span>
          </div>

          {variant === "floor" && (
            <div className="mt-4 flex items-start gap-2 bg-white/[0.03] rounded-2xl p-3.5">
              <HomeIcon size={15} className="text-kaizen-violetSoft shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-kaizen-violetSoft" data-testid="floor-home-copy">{mission.floor_home_line}</p>
                <button
                  onClick={switchToFull}
                  data-testid="override-to-full-button"
                  className="mt-2 text-xs text-kaizen-dim underline hover:text-kaizen-violetSoft transition-colors duration-300"
                >
                  Feeling up to it? Do the full session instead
                </button>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between mt-4 mb-2">
            <div className="flex items-center gap-1.5 text-kaizen-dim text-xs">
              <TimerIcon size={13} />
              <span data-testid="session-timer">{formatClock(elapsed)}</span>
            </div>
            <p className="text-[11px] text-kaizen-dim2" data-testid="pain-stop-line">{mission.pain_stop_line}</p>
          </div>

          <div data-testid="exercise-list">
            {exercises.map((ex) => (
              <ExerciseRow key={ex.id} ex={ex} checked={!!checked[ex.id]} onToggle={toggleCheck} onSwap={swapExercise} />
            ))}
          </div>

          {!completion ? (
            <div className="mt-6 grid grid-cols-3 gap-2">
              <button
                onClick={() => complete("full")}
                disabled={completing}
                data-testid="complete-did-it-button"
                className="glow-cta py-2.5 rounded-full bg-kaizen-violet text-[#140A24] text-xs font-semibold disabled:opacity-50"
              >
                Did it
              </button>
              <button
                onClick={() => complete("partial")}
                disabled={completing}
                data-testid="complete-did-some-button"
                className="py-2.5 rounded-full border border-white/15 text-xs text-kaizen-dim disabled:opacity-50"
              >
                Did some
              </button>
              <button
                onClick={() => complete("none")}
                disabled={completing}
                data-testid="complete-couldnt-button"
                className="py-2.5 rounded-full border border-white/15 text-xs text-kaizen-dim disabled:opacity-50"
              >
                Couldn't
              </button>
            </div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-6 text-center glass-card rounded-2xl py-4"
              data-testid="completion-success-state"
            >
              <p className="text-sm text-ready-green">{completion.message}</p>
              <p className="text-xs text-kaizen-dim mt-1">
                Streak: {completion.streak} · Consistency: {completion.consistency_pct}%
              </p>
            </motion.div>
          )}
        </div>

        {mission.day_type === "YELLOW" && (
          <p className="flex items-center gap-1.5 text-[11px] text-kaizen-dim2 mt-4 justify-center">
            <ShieldAlert size={11} /> One miss never zeroes your streak.
          </p>
        )}
      </div>

      <BottomNav />
    </div>
  );
}
